/**
 * `wayai bases credentials …` — the credentials a base's integrations authenticate with.
 *
 * The replacement for `bases secrets`, which addressed an organization-level vault nothing
 * reads any more. These rows belong to ONE base: a `credential:<name>` reference resolves
 * against the credentials of the base serving the request, so the same configuration
 * promoted or cloned elsewhere reads that base's own credential of that name. Hence every
 * verb here addresses a base (`--base`), which the vault's never did.
 *
 * Namespaced because WayAI already owns credential storage of its own — connection
 * credentials and organization credentials (docs/reference/rekor-platform-contracts.md
 * §3.2).
 *
 * **Secret values never travel in argv.** There is deliberately no `--value <value>`: an
 * argv element is readable from `/proc/<pid>/cmdline` by any local user on a default
 * `hidepid=0` host, and it lands in shell history and CI job logs. A value arrives from a
 * pipe (`--value-stdin`), a masked prompt, or `--file`; it goes straight into the request
 * body and is never echoed, logged, or folded into an error message — including the
 * "cannot be combined" error below, which names the FLAGS and never their contents. Every
 * response is metadata: the service answers a create, an update and a rotate with the row,
 * never the value, so printing a response is safe.
 *
 * TWO KINDS OF ROW, and the difference decides which verbs apply. A `direct` row was
 * created here and is deleted here. An `org-link` row mirrors a WayAI organization
 * credential — it is created by `link`, removed by `unlink`, and rotated by rotating the
 * organization credential, which reaches every base linked to it at once.
 *
 * `delete` and `rotate` both refuse an `org-link` row, but only ONE of those refusals comes
 * from the service: it rejects the delete, and does NOT reject the rotate. So `rotate`
 * checks the row here first. Without that check the write succeeds and installs a value the
 * organization credential does not know about — which the next organization-level rotation
 * silently overwrites, leaving an operator convinced they had rotated the key.
 *
 * `link` and `unlink` address the ORGANIZATION credential and so go through WayAI's own
 * setup API rather than the data surface, which is why they take `--org-credential` rather
 * than a row id. Both require a caller signed in as a PERSON — an API token cannot mint an
 * organization link — so they refuse a `WAYAI_TOKEN` session with that explanation. That flag is
 * spelled with the prefix deliberately: `--credential <id>` trips the tree walk in
 * `program.test.ts` that refuses any secret-shaped flag taking a value, and the guard errs
 * wide on purpose — a false positive costs a rename, a false negative leaks a credential.
 * The longer name also says what it takes, which is an id and never a value.
 */

import { Command } from 'commander';
import { readFileSync } from 'node:fs';
import { createDataClient } from '../client.js';
import { printOutput } from '../output.js';
import {
  isInteractive,
  outputFormat,
  pathSegment,
  readSecret,
  requireBase,
  withBaseOption,
} from '../helpers.js';
import { confirm } from '../../lib/utils.js';
import { expected } from '../../lib/expected.js';
import { BASE_CREDENTIAL_MANAGED_BY } from '@wayai/core/contracts';

interface ValueSourceOpts {
  file?: string;
  valueStdin?: boolean;
  valuePrompt?: boolean;
}

/** Declare the three ways a credential value may be supplied. Shared by `create` and `rotate`. */
function withValueSourceOptions(cmd: Command, what: string): Command {
  return cmd
    .option(
      '--file <path>',
      `Read ${what} from a file and base64-encode it (certificates, keystores, service-account JSON)`,
    )
    .option('--value-stdin', `Read ${what} from stdin (recommended for CI)`)
    .option('--value-prompt', `Prompt for ${what}, masked`);
}

/**
 * Resolve a credential value from exactly one of `--file` (the file's bytes,
 * base64-encoded) or an interactive/piped entry via `readSecret`.
 *
 * `--file` is compatible with neither entry flag, and saying so is worth an error rather
 * than a precedence rule: with `--file` silently winning, a CI job that piped the new
 * credential AND named a stale file would store the stale one and report success.
 */
async function resolveValue(opts: ValueSourceOpts, label: string): Promise<string> {
  if (opts.file !== undefined) {
    if (opts.valueStdin || opts.valuePrompt) {
      throw expected('--file cannot be combined with --value-stdin or --value-prompt — pass one.');
    }
    try {
      return readFileSync(opts.file).toString('base64');
    } catch (e) {
      // Names the PATH, never the contents.
      throw expected(`--file ${opts.file}: ${e instanceof Error ? e.message : 'could not be read'}`);
    }
  }
  return readSecret(
    {
      stdin: opts.valueStdin,
      prompt: opts.valuePrompt,
      stdinFlag: '--value-stdin',
      promptHint: '--value-prompt',
    },
    label,
  );
}

/**
 * The credential path for a base, with both ids contained.
 *
 * One builder for every verb: seven hand-rolled copies of the same template is where one
 * of them ends up spelling a segment or a label differently, and `pathSegment`'s label is
 * what names the offending flag when an id is rejected.
 */
function credentialPath(base: string, id?: string): string {
  const root = `/v1/${pathSegment(base, 'base id')}/credentials`;
  return id === undefined ? root : `${root}/${pathSegment(id, 'credential id')}`;
}

export function buildBasesCredentialsCommand(): Command {
  // `--base` on the GROUP rather than per verb: unlike `bases tokens`, every verb here
  // addresses one base, so declaring it once is what the flag actually means.
  const credentials = withBaseOption(
    new Command('credentials').description("Manage a base's integration credentials"),
  );

  withValueSourceOptions(
    credentials
      .command('create')
      .description('Store a credential on this base (value read from stdin, a masked prompt, or --file)')
      .requiredOption('--name <name>', 'Credential name — what a credential:<name> reference resolves')
      .option('--content-type <mime>', 'MIME type of the value (e.g. application/x-pkcs12)')
      .option(
        '--expires-at <date>',
        'Expiry as ISO-8601 (e.g. 2027-01-01T00:00:00Z) — informational; nothing is auto-disabled',
      )
      .option(
        '--no-sync-to-production',
        'Do not publish this credential on promote — production owns its own value',
      ),
    'the credential value',
  ).action(async function (
    this: Command,
    opts: ValueSourceOpts & {
      name: string;
      contentType?: string;
      expiresAt?: string;
      syncToProduction: boolean;
    },
  ) {
    const base = requireBase(this);
    const body: Record<string, unknown> = {
      name: opts.name,
      value: await resolveValue(opts, 'Credential value'),
    };
    if (opts.contentType) body.content_type = opts.contentType;
    if (opts.expiresAt) body.expires_at = opts.expiresAt;
    // Commander sets this false only when `--no-sync-to-production` was passed; the
    // service defaults it ON, so the field is sent only to turn it off.
    if (opts.syncToProduction === false) body.sync_to_production = false;
    const client = await createDataClient();
    printOutput(
      await client.request('POST', credentialPath(base), body),
      outputFormat(this),
    );
  });

  credentials
    .command('list')
    .description("List this base's credentials (values are never returned)")
    .action(async function (this: Command) {
      const base = requireBase(this);
      const client = await createDataClient();
      printOutput(
        await client.request('GET', credentialPath(base)),
        outputFormat(this),
      );
    });

  credentials
    .command('get <id>')
    .description('Get credential metadata (the value is never returned)')
    .action(async function (this: Command, id: string) {
      const base = requireBase(this);
      const client = await createDataClient();
      printOutput(
        await client.request('GET', credentialPath(base, id)),
        outputFormat(this),
      );
    });

  withValueSourceOptions(
    credentials
      .command('rotate <id>')
      .description('Install a new caller-supplied value (never auto-generates)')
      .option('--expires-at <date>', "Set the rotated credential's new expiry (ISO-8601)"),
    'the new credential value',
  ).action(async function (this: Command, id: string, opts: ValueSourceOpts & { expiresAt?: string }) {
    const base = requireBase(this);
    const client = await createDataClient();

    // Read BEFORE prompting for a value: an operator asked for a secret and then told the
    // request was pointless has already typed it.
    const existing = await client.request<{ managed_by?: string; name?: string }>(
      'GET',
      credentialPath(base, id),
    );
    if (existing?.managed_by === BASE_CREDENTIAL_MANAGED_BY.ORG_LINK) {
      throw expected(
        `"${existing.name ?? id}" mirrors an organization credential. Rotate the organization `
          + 'credential instead — that reaches every base linked to it. Rotating it here would '
          + 'be overwritten by the next organization-level rotation.',
      );
    }

    const body: Record<string, unknown> = { value: await resolveValue(opts, 'New credential value') };
    if (opts.expiresAt) body.expires_at = opts.expiresAt;
    printOutput(
      await client.request(
        'POST',
        `${credentialPath(base, id)}/rotate`,
        body,
      ),
      outputFormat(this),
    );
  });

  credentials
    .command('set-sync <id>')
    .description(
      'Set the promote-time publish flag on a direct credential: on by default, '
        + 'off with --no-sync-to-production',
    )
    // Commander's own boolean negation, the SAME spelling `create` uses for this flag — a
    // `--enabled <bool>` string parsed by hand was a second grammar for one setting.
    // Declared as the negation alone: declaring both forms makes a bare invocation yield
    // `undefined` rather than the default.
    .option('--no-sync-to-production', 'Do not publish this credential on promote')
    .action(async function (this: Command, id: string, opts: { syncToProduction: boolean }) {
      const base = requireBase(this);
      const client = await createDataClient();
      printOutput(
        await client.request('PUT', credentialPath(base, id), {
          sync_to_production: opts.syncToProduction,
        }),
        outputFormat(this),
      );
    });

  credentials
    .command('delete <id>')
    .description('Delete a direct credential (an organization-linked one is unlinked instead)')
    .option('-y, --yes', 'Skip confirmation prompt')
    .action(async function (this: Command, id: string, opts: { yes?: boolean }) {
      const base = requireBase(this);
      // Off a TTY the prompt has no one to answer it, so `--yes` is required rather than
      // assumed in either direction.
      if (!opts.yes) {
        if (!isInteractive()) {
          throw expected('Refusing to delete without confirmation — pass --yes to delete non-interactively.');
        }
        if (!(await confirm(`Delete credential ${id} on base ${base}? This cannot be undone.`))) {
          console.log('Aborted');
          return;
        }
      }
      const client = await createDataClient();
      // No `?unlink=true`, ever, from here. The flag is an expressed intent the service
      // refuses outright from a caller that may not unlink, so sending it speculatively
      // turns an ordinary delete into a refusal. An organization-linked row answers 409
      // and the operator is told to use `unlink`.
      await client.request('DELETE', credentialPath(base, id));
      printOutput({ id, deleted: true }, outputFormat(this));
    });

  credentials
    .command('link')
    .description('Project an organization credential onto this base')
    .requiredOption('--org-credential <id>', 'Organization credential id')
    .option('--name <name>', "Credential name on the base (default: a slug of the credential's name)")
    .action(async function (this: Command, opts: { orgCredential: string; name?: string }) {
      const base = requireBase(this);
      const client = await createDataClient();
      // WayAI's own setup API, not the data surface: the organization credential is
      // WayAI's, and only that route decrypts it, applies the tag/environment bind gate,
      // and records the link that makes a later rotation reach this base.
      printOutput(
        await client.linkBaseCredential(opts.orgCredential, {
          base_id: base,
          ...(opts.name ? { name: opts.name } : {}),
        }),
        outputFormat(this),
      );
    });

  credentials
    .command('unlink <id>')
    .description('Remove an organization credential\'s projection from this base')
    .requiredOption('--org-credential <id>', 'Organization credential id')
    .action(async function (this: Command, id: string, opts: { orgCredential: string }) {
      const base = requireBase(this);
      const client = await createDataClient();
      await client.unlinkBaseCredential(opts.orgCredential, id, { base_id: base });
      printOutput({ id, unlinked: true }, outputFormat(this));
    });

  return credentials;
}
